import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import SEOHead from '../common/SEOHead';
import { getGuideArticleSEO } from '../../utils/seo';
import { useInView } from '../../hooks/useInView';
import AuthorByline from '../common/AuthorByline';
import GuideMarkdown from '../common/GuideMarkdown';
import { getGuide, getCategory, getRelatedGuides } from '../../data/guides';

const Reveal: React.FC<{ children: React.ReactNode; delay?: number; className?: string }> = ({
  children,
  delay = 0,
  className = '',
}) => {
  const { ref, inView } = useInView<HTMLDivElement>({ threshold: 0.1 });
  return (
    <div
      ref={ref}
      className={`transition-all duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

// Strip the leading H1 from the body — we render it ourselves in the hero
// so we get the bigger italic display treatment + breadcrumb above it.
function splitHeading(body: string): { heading: string; rest: string } {
  const lines = body.split('\n');
  let heading = '';
  let restStart = 0;
  for (let i = 0; i < Math.min(5, lines.length); i++) {
    if (lines[i].startsWith('# ')) {
      heading = lines[i].slice(2).trim();
      restStart = i + 1;
      break;
    }
  }
  // Skip blank lines after the H1 so the first paragraph leads cleanly.
  while (restStart < lines.length && lines[restStart].trim() === '') restStart++;
  return { heading, rest: lines.slice(restStart).join('\n') };
}

const GuideArticle: React.FC = () => {
  const { category, slug } = useParams<{ category: string; slug: string }>();
  const cat = category ? getCategory(category) : null;
  const guide = slug ? getGuide(slug) : null;

  if (!cat || !guide || guide.category !== cat.id) {
    return <Navigate to="/guides" replace />;
  }

  const { heading, rest } = splitHeading(guide.body);
  const displayTitle = heading || guide.title;
  const related = getRelatedGuides(guide.slug, 3);

  return (
    <div className="bg-cream-50 min-h-screen">
      <SEOHead seoData={getGuideArticleSEO(cat.id, cat.label, guide.slug, guide.title, guide.excerpt)} />

      {/* ===== Hero ===== */}
      <section className="relative bg-cream-50 border-b border-warm-200 overflow-hidden">
        <div className="absolute -top-10 -right-10 w-80 h-80 pointer-events-none">
          <div className="w-full h-full bg-gradient-to-bl from-forest-100/30 to-transparent rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-3xl mx-auto px-6 sm:px-8 py-14 md:py-20">
          <nav className="text-[11px] font-accent italic text-warm-500 mb-8 tracking-wide">
            <Link to="/" className="hover:text-gold-700 transition-colors">Home</Link>
            <span className="mx-2 text-gold-500">•</span>
            <Link to="/guides" className="hover:text-gold-700 transition-colors">Guides</Link>
            <span className="mx-2 text-gold-500">•</span>
            <Link to={`/guides/${cat.id}`} className="hover:text-gold-700 transition-colors">{cat.label}</Link>
          </nav>

          <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.4em] text-gold-700 font-accent italic mb-5">
            — {cat.label} —
          </div>
          <h1 className="font-display italic text-[32px] sm:text-[44px] md:text-[52px] text-forest-900 leading-[1.05] tracking-tight mb-7">
            {displayTitle}
          </h1>

          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.2em] text-warm-500 font-semibold">
            <span>{guide.readMinutes} min read</span>
            <span className="text-warm-300">·</span>
            <span>{guide.wordCount.toLocaleString()} words</span>
            <span className="text-warm-300">·</span>
            <span className="font-accent italic normal-case tracking-normal text-warm-500">LiberaCure Editorial</span>
          </div>
        </div>
      </section>

      {/* ===== Body ===== */}
      <section className="py-12 sm:py-16">
        <div className="max-w-3xl mx-auto px-6 sm:px-8">
          <article>
            <GuideMarkdown content={rest} />
          </article>

          {/* Author byline at the article bottom — reuses the existing component
              so signature/branding stays consistent across all editorial pages. */}
          <div className="mt-12 pt-8 border-t border-warm-200">
            <AuthorByline />
          </div>
        </div>
      </section>

      {/* ===== Cross-link to shop ===== */}
      <section className="bg-cream-100/60 border-y border-warm-200">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-12 sm:py-16">
          <Reveal>
            <div className="flex items-center justify-between gap-6 flex-wrap">
              <div>
                <span className="text-nano font-semibold text-gold-700 uppercase tracking-[0.2em] mb-2 block">
                  If you&rsquo;re ready
                </span>
                <h3 className="font-display text-heading-lg text-forest-900 tracking-tight">
                  Browse {cat.label.toLowerCase()} options
                </h3>
                <p className="text-body-sm text-warm-500 mt-1.5">
                  The protocol above &mdash; without the markup.
                </p>
              </div>
              <Link to={cat.shopLink} className="btn-primary gap-2">
                See products
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== Related ===== */}
      {related.length > 0 && (
        <section className="py-16 sm:py-20">
          <div className="max-w-5xl mx-auto px-6 sm:px-8">
            <div className="text-center mb-10">
              <span className="text-nano font-semibold text-forest-700 uppercase tracking-[0.2em] mb-3 block">
                Keep reading
              </span>
              <h2 className="font-display text-[26px] sm:text-display-sm text-forest-900 tracking-tight">
                More from the editorial.
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {related.map((g, idx) => {
                const rcat = getCategory(g.category);
                return (
                  <Reveal key={g.slug} delay={idx * 60}>
                    <Link
                      to={`/guides/${g.category}/${g.slug}`}
                      className="group block h-full bg-cream-50 rounded-xl border border-warm-200 p-6 hover:shadow-hover hover:-translate-y-0.5 hover:border-gold-400/50 transition-all duration-500 ease-spring"
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-[10px] font-semibold text-gold-700 uppercase tracking-[0.18em]">
                          {rcat?.label || g.category}
                        </span>
                        <span className="text-warm-300">·</span>
                        <span className="font-accent italic text-[11px] text-warm-500">
                          {g.readMinutes} min
                        </span>
                      </div>
                      <h3 className="font-display text-[18px] text-forest-900 tracking-tight leading-snug mb-2 group-hover:text-gold-800 transition-colors">
                        {g.title}
                      </h3>
                      <p className="text-[13px] text-warm-600 leading-[1.65] line-clamp-3">
                        {g.excerpt}
                      </p>
                    </Link>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default GuideArticle;
